import React, { useState, useEffect } from 'react';
import { FaPaperPlane, FaUsers, FaBell, FaHistory, FaCheckCircle, FaChevronDown, FaChevronUp, FaTimes } from 'react-icons/fa';
import ApiService from '../models/ApiService';

function SendNotification() {
    const [formData, setFormData] = useState({
        title: '',
        body: ''
    });

    const [isSending, setIsSending] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [notificationHistory, setNotificationHistory] = useState([]);
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const formatDateToDDMMYYYY = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year} `;
    };

    // Fetch notification history from API
    useEffect(() => {
        fetchNotificationHistory();
    }, []);

    const fetchNotificationHistory = async () => {
        try {
            const response = await ApiService.GetNotification();
            const logs = response?.data?.responseContent?.logs;
            if (logs && Array.isArray(logs)) {
                const sortedLogs = logs.sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                );
                setNotificationHistory(sortedLogs);
            } else {
                throw new Error("Invalid response format");
            }
        } catch (err) {
            console.error("Error fetching notification history:", err);
            setError("Failed to load notification history");
        }
    };

    const toggleExpand = (id) => {
        const newExpandedItems = new Set(expandedItems);
        if (newExpandedItems.has(id)) {
            newExpandedItems.delete(id);
        } else {
            newExpandedItems.add(id);
        }
        setExpandedItems(newExpandedItems);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate form data
        if (!formData.title.trim() || !formData.body.trim()) {
            setError('Title and body are required.');
            return;
        }

        setShowConfirmModal(true);
    };

    const confirmSendNotification = async () => {
        setIsSending(true);
        setError(null);
        setShowConfirmModal(false);

        try {
            const response = await ApiService.PushNotification({
                title: formData.title.trim(),
                body: formData.body.trim()
            });
            console.log(response);


            if (response.status != 200) {
                throw new Error(response.message || 'Failed to send notification');
            }

            const newNotification = {
                _id: `temp-${Date.now()}`,
                title: formData.title,
                body: formData.body,
                status: 'success',
                createdAt: new Date().toISOString(),
                sent_to: 0,
                time: new Date().toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                })
            };

            setNotificationHistory(prev => [newNotification, ...prev]);
            setIsSent(true);

            setTimeout(() => {
                setIsSent(false);
                setFormData({
                    title: '',
                    body: ''
                });
            }, 3000);
        } catch (err) {
            console.error('Error sending notification:', err);
            setError(err.message || 'Failed to send notification. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const cancelSendNotification = () => {
        setShowConfirmModal(false);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'sent': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent': return <FaCheckCircle className="text-green-500" />;
            default: return <FaCheckCircle className="text-gray-500" />;
        }
    };

    return (
        <div className="min-h-screen py-8 px-4" id='primary-fonts'>
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Confirm Notification</h3>
                            <button
                                onClick={cancelSendNotification}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-600 mb-2">Are you sure you want to send this notification to all users?</p>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium text-gray-800 mb-1">{formData.title}</h4>
                                <p className="text-gray-600 text-sm">{formData.body}</p>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={cancelSendNotification}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSendNotification}
                                className="px-4 py-2 bg-[--primary-btn-color] text-white rounded-lg hover:bg-[--secondary-btn-color] transition-colors flex items-center"
                            >
                                <FaPaperPlane className="mr-2" />
                                Send Notification
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="bg-[--header-color-g] rounded-xl shadow-sm p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-white flex items-center">
                                <FaBell className="mr-3 text-white" />
                                Send Notification
                            </h1>
                            <p className="text-white mt-2">Send important updates to all users</p>
                        </div>
                        <div className="bg-white text-[--text-color-g] px-4 py-2 rounded-full text-sm font-semibold">
                            <FaUsers className="inline mr-2" />
                            All Users
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
                        <FaBell className="mr-2" />
                        {error}
                    </div>
                )}

                {/* Success Message */}
                {isSent && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center">
                        <FaPaperPlane className="mr-2" />
                        Notification sent successfully to all users!
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Form Section */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Title */}
                                <div>
                                    <label className="block font-medium text-black mb-2">
                                        Notification Title
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg "
                                        placeholder="Enter notification title"
                                    />
                                </div>

                                {/* Message */}
                                <div>
                                    <label className="block font-medium text-black mb-2">
                                        Message
                                    </label>
                                    <textarea
                                        name="body"
                                        value={formData.body}
                                        onChange={handleInputChange}
                                        required
                                        rows="5"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg "
                                        placeholder="Write your notification message here..."
                                    />
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSending}
                                    className="w-full bg-[--primary-btn-color] text-white py-3 px-6 rounded-lg font-semibold 
                                    hover:bg-[--secondary-btn-color] focus:outline-none focus:ring-2 focus:ring-[--primary-btn-color] focus:ring-offset-2 flex items-center justify-center disabled:opacity-50 transition duration-200"
                                >
                                    {isSending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                            Sending to all users...
                                        </>
                                    ) : (
                                        <>
                                            <FaPaperPlane className="mr-2" />
                                            Send to All Users
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>

                    {/* History Section */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-xl shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-black mb-4 flex items-center">
                                <FaHistory className="mr-2 text-[--text-color-g]" />
                                Recent Notifications
                            </h3>

                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {notificationHistory.length > 0 ? (
                                    notificationHistory.map((item) => (
                                        <div key={item._id || item.id} className="border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                                            <div
                                                className="p-3 cursor-pointer"
                                                onClick={() => toggleExpand(item._id || item.id)}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <h4 className="font-medium text-[--text-color-g] text-sm">{item.title}</h4>
                                                    <span className={`px-2 py-1 rounded-full text-xs text-[--text-color-g] font-medium ${getStatusColor(item.status)}`}>
                                                        {item.status}
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-black">
                                                    <span>{item.createdAt ? formatDateToDDMMYYYY(item.createdAt) : item.date}</span>
                                                    <div className="flex items-center">
                                                        {getStatusIcon(item.status)}
                                                        {expandedItems.has(item._id || item.id) ? (
                                                            <FaChevronUp className="ml-2 text-gray-400" />
                                                        ) : (
                                                            <FaChevronDown className="ml-2 text-gray-400" />
                                                        )}
                                                    </div>
                                                </div>
                                                {item.sent_to && (
                                                    <div className="mt-2 text-xs text-gray-500">
                                                        Sent to: {item.sent_to} users
                                                    </div>
                                                )}
                                            </div>

                                            {(expandedItems.has(item._id || item.id)) && (
                                                <div className="px-3 pb-3 pt-2 border-t border-gray-100">
                                                    <div className="bg-gray-50 rounded-lg p-3">
                                                        <h5 className="text-xs font-semibold text-gray-700 mb-1">Message:</h5>
                                                        <p className="text-sm text-[--text-color]">{item.body || item.message}</p>
                                                        {item.sendBy && (
                                                            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-black">
                                                                <div>
                                                                    <span className="font-semibold">Sent by:</span> {item.sendBy.username}
                                                                </div>
                                                                <div>
                                                                    <span className="font-semibold">Time:</span> {item.time}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-8 text-gray-500">
                                        <FaHistory className="text-3xl mx-auto mb-2 text-gray-400" />
                                        <p>No notification history yet</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SendNotification;